import React, { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import api from '../../services/api';
import Modal from '../Modal';
import { useAuth } from '../../context/AuthContext';

// Agente de retención (IVA / ISLR / Municipal) + datos del comprobante
// de la empresa activa. Antes estaban en el formulario de empresa de
// Administración; ahora los mantiene el contador (o superior) acá.
const FORM_VACIO = {
  agente_retencion: false, agente_retencion_islr: false, agente_retencion_municipal: false,
  // Apagado = 2% por defecto; encendido = % propio de la empresa (según la alcaldía)
  municipal_personalizado: false, retencion_municipal_pct: '',
  numero_providencia_retencion: '', fecha_providencia_retencion: '', direccion_fiscal: ''
};

function SiNo({ valor }) {
  return (
    <span className={valor ? 'font-medium text-green-700' : 'text-slate-400'}>
      {valor ? 'Sí' : 'No'}
    </span>
  );
}

export default function AgenteRetencionEmpresa() {
  const { empresaActiva, empresaActivaNombre, refrescarSesion } = useAuth();
  const [datos, setDatos] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState('');

  function cargar() {
    api.get('/empresas/fiscal').then(({ data }) => setDatos(data));
  }

  useEffect(() => {
    if (empresaActiva) cargar();
  }, [empresaActiva]);

  function empezarEdicion() {
    setForm({
      agente_retencion: !!datos.agente_retencion,
      agente_retencion_islr: !!datos.agente_retencion_islr,
      agente_retencion_municipal: !!datos.agente_retencion_municipal,
      municipal_personalizado: datos.retencion_municipal_pct != null,
      retencion_municipal_pct: datos.retencion_municipal_pct != null ? String(Number(datos.retencion_municipal_pct)) : '',
      numero_providencia_retencion: datos.numero_providencia_retencion || '',
      fecha_providencia_retencion: datos.fecha_providencia_retencion ? String(datos.fecha_providencia_retencion).slice(0, 10) : '',
      direccion_fiscal: datos.direccion_fiscal || ''
    });
    setMostrarForm(true);
  }

  function cancelar() {
    setMostrarForm(false);
    setForm(FORM_VACIO);
    setError('');
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    try {
      const { municipal_personalizado, ...resto } = form;
      const { data } = await api.put('/empresas/fiscal', {
        ...resto,
        retencion_municipal_pct: municipal_personalizado ? form.retencion_municipal_pct : null
      });
      setDatos(data);
      cancelar();
      // Facturas de compra leen los flags de agente desde la sesión
      refrescarSesion();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
    }
  }

  const checkbox = (campo, label) => (
    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
      <input
        type="checkbox"
        checked={!!form[campo]}
        onChange={(e) => setForm({ ...form, [campo]: e.target.checked })}
        className="w-4 h-4 accent-primary-600 cursor-pointer"
      />
      {label}
    </label>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between p-4 pb-3">
        <div>
          <h2 className="font-semibold text-slate-700">Agente de retención — {empresaActivaNombre}</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Define qué retenciones aplica esta empresa en sus facturas de compra y los datos del comprobante.
          </p>
        </div>
        {datos && (
          <button onClick={empezarEdicion} title="Editar" className="text-slate-400 hover:text-primary-600 transition-colors">
            <Pencil size={16} />
          </button>
        )}
      </div>

      {datos && (
        <div className="px-4 pb-4 text-sm text-slate-600 space-y-2">
          <p>
            Retención IVA: <SiNo valor={datos.agente_retencion} />
            {' · '}ISLR: <SiNo valor={datos.agente_retencion_islr} />
            {' · '}Municipal: <SiNo valor={datos.agente_retencion_municipal} />
            {!!datos.agente_retencion_municipal && (
              <span className="text-slate-500">
                {' '}({datos.retencion_municipal_pct != null ? `${Number(datos.retencion_municipal_pct)}% personalizado` : '2% por defecto'})
              </span>
            )}
          </p>
          <p>
            <span className="text-slate-400">Providencia SENIAT:</span>{' '}
            {datos.numero_providencia_retencion || '-'}
            {datos.fecha_providencia_retencion && ` (${String(datos.fecha_providencia_retencion).slice(0, 10)})`}
          </p>
          <p>
            <span className="text-slate-400">Dirección fiscal:</span> {datos.direccion_fiscal || '-'}
          </p>
        </div>
      )}

      {mostrarForm && (
        <Modal title="Editando agente de retención" onClose={cancelar}>
          <form onSubmit={guardar} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              {checkbox('agente_retencion', 'Agente de retención de IVA')}
              {checkbox('agente_retencion_islr', 'Agente de retención de ISLR')}
              {checkbox('agente_retencion_municipal', 'Agente de retención Municipal')}
              {form.agente_retencion_municipal && (
                <div className="pl-6 space-y-2">
                  {checkbox('municipal_personalizado', '% municipal personalizado (si no, 2%)')}
                  {form.municipal_personalizado && (
                    <label className="text-sm text-slate-600 flex items-center gap-2">
                      % de retención municipal
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        required
                        value={form.retencion_municipal_pct}
                        onChange={(e) => setForm({ ...form, retencion_municipal_pct: e.target.value })}
                        className="border rounded-md px-3 py-2 w-28"
                      />
                    </label>
                  )}
                </div>
              )}
            </div>
            <input
              placeholder="N° de Providencia SENIAT (agente de retención IVA)"
              value={form.numero_providencia_retencion}
              onChange={(e) => setForm({ ...form, numero_providencia_retencion: e.target.value })}
              className="border rounded-md px-3 py-2"
            />
            <label className="text-sm text-slate-600 flex flex-col gap-1">
              Fecha de la Providencia
              <input
                type="date"
                value={form.fecha_providencia_retencion}
                onChange={(e) => setForm({ ...form, fecha_providencia_retencion: e.target.value })}
                className="border rounded-md px-3 py-2"
              />
            </label>
            <input
              placeholder="Dirección fiscal (para el comprobante de retención)"
              value={form.direccion_fiscal}
              onChange={(e) => setForm({ ...form, direccion_fiscal: e.target.value })}
              className="border rounded-md px-3 py-2 sm:col-span-2"
            />
            {error && <p className="text-red-600 text-sm sm:col-span-2">{error}</p>}
            <button type="submit" className="bg-primary-600 text-white rounded-md py-2 sm:col-span-2 hover:bg-primary-700">
              Guardar cambios
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
